/**
 * Translation Service
 * Translates user-generated text via MyMemory free API.
 * For production, swap to a Supabase Edge Function proxying Google/DeepL.
 */

const MYMEMORY_API = 'https://api.mymemory.translated.net/get'
const MAX_CHUNK_LENGTH = 480

export interface TranslationResult {
  translatedText: string
  detectedSourceLang?: string
}

function splitIntoChunks(text: string): string[] {
  if (text.length <= MAX_CHUNK_LENGTH) return [text]

  const chunks: string[] = []
  let remaining = text

  while (remaining.length > MAX_CHUNK_LENGTH) {
    let splitAt = -1

    // Try paragraph break first
    const paraIdx = remaining.lastIndexOf('\n\n', MAX_CHUNK_LENGTH)
    if (paraIdx > 100) {
      splitAt = paraIdx + 2
    } else {
      // Try single newline
      const nlIdx = remaining.lastIndexOf('\n', MAX_CHUNK_LENGTH)
      if (nlIdx > 100) {
        splitAt = nlIdx + 1
      } else {
        // Try sentence boundary (. ! ?)
        const sentenceMatch = remaining.slice(0, MAX_CHUNK_LENGTH).match(/.*[.!?]\s/s)
        if (sentenceMatch && sentenceMatch[0].length > 100) {
          splitAt = sentenceMatch[0].length
        } else {
          // Last resort: split at last space
          const spaceIdx = remaining.lastIndexOf(' ', MAX_CHUNK_LENGTH)
          splitAt = spaceIdx > 50 ? spaceIdx + 1 : MAX_CHUNK_LENGTH
        }
      }
    }

    chunks.push(remaining.slice(0, splitAt))
    remaining = remaining.slice(splitAt)
  }

  if (remaining) chunks.push(remaining)
  return chunks
}

async function translateChunk(
  chunk: string,
  langPair: string
): Promise<string> {
  const url = `${MYMEMORY_API}?q=${encodeURIComponent(chunk)}&langpair=${langPair}`
  const res = await fetch(url)

  if (!res.ok) {
    throw new Error(`Translation request failed: ${res.status}`)
  }

  const data = await res.json()

  if (data.responseStatus !== 200 && data.responseStatus !== '200') {
    throw new Error(data.responseDetails || 'Translation failed')
  }

  return data.responseData.translatedText
}

export async function translateText(
  text: string,
  targetLang: string,
  sourceLang = 'auto'
): Promise<TranslationResult> {
  const langPair =
    sourceLang === 'auto' ? `en|${targetLang}` : `${sourceLang}|${targetLang}`

  const chunks = splitIntoChunks(text)
  const translated = await Promise.all(
    chunks.map((chunk) => translateChunk(chunk, langPair))
  )

  return {
    translatedText: translated.join(''),
  }
}
